import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import DeckCard from '../components/DeckCard';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchSampleDecks } from '../lib/sanity';

/**
 * SampleDecks page - Browse and load sample decks
 * Backed by the Sanity `sampleDeck` document type.
 */
export default function SampleDecks() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeckId, setLoadingDeckId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const result = await fetchSampleDecks();
      if (!cancelled) {
        setDecks(result);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLoadDeck = async (deck) => {
    if (!deck.deckCode) {
      alert(t('sampleDecks.noDeckCode') || 'This deck is not yet available.');
      return;
    }
    setLoadingDeckId(deck.id);
    navigate(`/deck-builder?load=${encodeURIComponent(deck.deckCode)}`);
  };

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="h-8 w-8" />
            <h1 className="text-3xl font-bold">{t('sampleDecks.title') || 'Sample Decks'}</h1>
          </div>
          <p className="text-white/80 max-w-2xl">
            {t('sampleDecks.subtitle') ||
              'Browse our collection of sample decks to get started or find inspiration for your own builds. Click on any deck to view its contents or load it into the deck builder.'}
          </p>
        </div>
      </div>

      {/* Decks Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            {loading
              ? (t('common.loading') || 'Loading…')
              : ((t('sampleDecks.deckCount') || '{{count}} decks available').replace('{{count}}', decks.length))}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/deck-builder')}
            className="gap-2"
          >
            <Sparkles className="h-4 w-4" />
            {t('sampleDecks.createYourOwn') || 'Create Your Own'}
          </Button>
        </div>

        {/* Deck Cards Grid */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />
          </div>
        ) : decks.length === 0 ? (
          <div className="text-center py-16">
            <Layers className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Sample Decks Yet</h2>
            <p className="text-muted-foreground mb-4">
              Check back soon for sample deck builds!
            </p>
            <Button onClick={() => navigate('/deck-builder')}>
              Build Your Own Deck
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {decks.map((deck) => (
              <DeckCard
                key={deck.id}
                deck={deck}
                language={language}
                onLoadDeck={handleLoadDeck}
                loading={loadingDeckId === deck.id}
                showViewButton={true}
                showLoadButton={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
